/**
 * Node Hover Context
 *
 * Tracks which node is currently being hovered for edge visibility.
 * Separate from CanvasContext per BUILD_ORDER deliverables.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react'

export interface NodeHoverContextValue {
  hoveredNodeId: string | null
  setHoveredNodeId: (id: string | null) => void
}

export const NodeHoverContext = createContext<NodeHoverContextValue>({
  hoveredNodeId: null,
  setHoveredNodeId: () => {},
})

/**
 * Hook to read hover state. Never throws.
 */
export function useNodeHover(): NodeHoverContextValue {
  return useContext(NodeHoverContext)
}

/**
 * Hook to create hover context value - use in the canvas provider.
 * Uses useState + useCallback for stable identity.
 */
export function useNodeHoverState(): NodeHoverContextValue {
  const [hoveredNodeId, setHoveredNodeIdState] = useState<string | null>(null)

  const setHoveredNodeId = useCallback((id: string | null) => {
    setHoveredNodeIdState(id)
  }, [])

  return useMemo(() => ({
    hoveredNodeId,
    setHoveredNodeId,
  }), [hoveredNodeId, setHoveredNodeId])
}
